import React, { useState } from 'react';
import type { Task } from '../../types/task.types';
import './VoicemailToAffaireModal.styles.css';

interface VoicemailToAffaireModalProps {
  isOpen: boolean;
  task: Task;
  onClose: () => void;
  /** Valide la tâche — affaireId null = « valider sans affaire ». */
  onValidate: (affaireId: number | null) => void;
}

/**
 * Validation d'une tâche issue d'un message vocal (page Appels →
 * « envoyer en tâche ») : proposer de lier un n° d'affaire au moment de
 * la validation — FACULTATIF. Si renseigné, le backend attribue aussi le
 * message vocal à l'affaire (visible dans la chronologie du tracker).
 * Pendant de EmailToAffaireModal pour les tâches issues d'emails.
 */
export const VoicemailToAffaireModal: React.FC<VoicemailToAffaireModalProps> = ({
  isOpen,
  task,
  onClose,
  onValidate
}) => {
  const [affaireInput, setAffaireInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const affaireId = parseInt(affaireInput, 10);
  const hasValidAffaire = Number.isFinite(affaireId) && affaireId > 0;

  const handleValidate = (withAffaire: boolean) => {
    setIsSubmitting(true);
    onValidate(withAffaire && hasValidAffaire ? affaireId : null);
  };

  return (
    <div className="vm-affaire-modal-overlay" onClick={onClose}>
      <div className="vm-affaire-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vm-affaire-modal-header">
          <h2>Valider la tâche</h2>
          <button className="vm-affaire-modal-close" onClick={onClose}>&times;</button>
        </div>

        <p className="vm-affaire-modal-description">
          Cette tâche provient d'un message vocal. Voulez-vous la lier à un
          numéro d'affaire&nbsp;? (facultatif)
        </p>

        <form
          className="vm-affaire-modal-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (hasValidAffaire) handleValidate(true);
          }}
        >
          <label htmlFor="vm-affaire-id">Numéro d'affaire</label>
          <input
            id="vm-affaire-id"
            type="text"
            inputMode="numeric"
            value={affaireInput}
            onChange={(e) => setAffaireInput(e.target.value.replace(/\D/g, ''))}
            placeholder="Ex: 79012"
            autoFocus
          />
          <small className="vm-affaire-form-hint">
            Si renseigné, le message vocal sera attribué à cette affaire et
            apparaîtra dans son tracker.
          </small>

          <div className="vm-affaire-modal-actions">
            <button
              type="button"
              className="vm-affaire-btn vm-affaire-btn-secondary"
              disabled={isSubmitting}
              onClick={() => handleValidate(false)}
            >
              Valider sans affaire
            </button>
            <button
              type="submit"
              className="vm-affaire-btn vm-affaire-btn-primary"
              disabled={isSubmitting || !hasValidAffaire}
            >
              {isSubmitting ? 'Validation…' : 'Lier et valider'}
            </button>
          </div>
        </form>

        {/* Rappel du contenu pour valider en connaissance de cause */}
        <div className="vm-affaire-task-preview">{task.message}</div>
      </div>
    </div>
  );
};

export default VoicemailToAffaireModal;
